// Make the model's output safe to publish.
//
// `generateJSON` does `JSON.parse(...) as T` — a cast, not a check. Everything
// downstream then trusted it: `engine.ts` copied `data.headline` straight into
// `company_posts.title` (text, no length limit), the reader rendered that raw at
// 3rem with no line-clamp, and the same string became the email subject.
//
// So when the model drifted and wrote the entire letter into `headline`, the
// dispatch for 2026-07-23 shipped with a 1003-character title. And because
// `body_md` was null and the reader's render gate needs one of stats/shipped/
// thread, an edition that came back with nothing usable — 2026-07-25 returned
// only {date, signoff, signrole, provenance} — published a page with a fallback
// title and a literally empty body.
//
// Both symptoms, one cause: nobody checked. Worse, `lastDispatch()` feeds the
// previous headline into tomorrow's prompt, so a drifted shape taught the next
// edition to drift too.
//
// This module is the check. It is pure so it can be tested against the real
// broken rows, and it never throws — a dispatch that arrives malformed should
// still publish whatever was salvageable, in the right fields.

import type { DispatchData, DispatchZone } from '../company/types';

/** A headline is one line of weather. Past this it is an essay in a title slot. */
export const MAX_HEADLINE = 80;
/** A dek is one sentence under it. */
export const MAX_DEK = 200;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** Collapse whitespace; a title must never carry newlines into a heading. */
const flat = (v: unknown): string => str(v).replace(/\s+/g, ' ').trim();

/**
 * Cut `s` to at most `max` characters on a word boundary.
 * Returns the kept head and the remainder, so nothing is thrown away.
 */
export function clip(s: string, max: number): { head: string; rest: string } {
  if (s.length <= max) return { head: s, rest: '' };
  const window = s.slice(0, max + 1);

  // Prefer a CLAUSE boundary. A dispatch headline is nearly always a list —
  // "resonance v2 and netti chat land on main, context compiler green, roadmap
  // reprojected" — and cutting that at a word boundary leaves a title ending
  // mid-thought and a one-word fragment for a dek. Cutting at the comma leaves
  // a headline that reads as a finished sentence and a remainder that reads as
  // its own clause.
  const clause = Math.max(
    window.lastIndexOf(', '),
    window.lastIndexOf('; '),
    window.lastIndexOf(' — '),
  );
  const word = window.lastIndexOf(' ');
  const cut = clause >= max * 0.45 ? clause : word >= max * 0.4 ? word : max;

  return { head: s.slice(0, cut).replace(/[\s,;:—-]+$/, ''), rest: s.slice(cut).replace(/^[\s,;:—-]+/, '').trim() };
}

/** The first sentence of `s`, and whatever followed it. */
function firstSentence(s: string): { head: string; rest: string } {
  // `flat()` has already collapsed newlines, so [\s\S] is only here because the
  // tsconfig targets ES2017 and the `s` (dotAll) flag isn't available.
  const m = /^([\s\S]+?[.!?])(\s+)(\S[\s\S]*)$/.exec(s);
  if (!m) return { head: s, rest: '' };
  return { head: m[1].replace(/[.]$/, ''), rest: m[3] };
}

function coerceItem(v: unknown): { title: string; note?: string } | null {
  if (typeof v === 'string') {
    const title = flat(v);
    return title ? { title } : null;
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const title = flat(o.title ?? o.name ?? o.label ?? o.summary);
  if (!title) return null;
  const note = flat(o.note ?? o.detail ?? o.description);
  return note ? { title, note } : { title };
}

function coerceItems(v: unknown): { title: string; note?: string }[] {
  if (!Array.isArray(v)) return [];
  return v.map(coerceItem).filter((x): x is { title: string; note?: string } => x !== null);
}

function coerceStats(v: unknown): { value: string | number; label: string }[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((s) => {
      if (!s || typeof s !== 'object') return null;
      const o = s as Record<string, unknown>;
      const value = o.value ?? o.count ?? o.n;
      const label = flat(o.label ?? o.name);
      if (value == null || !label) return null;
      return { value: typeof value === 'number' ? value : flat(value), label };
    })
    .filter((x): x is { value: string | number; label: string } => x !== null);
}

/** A day has a handful of places it grew, never a dozen. */
export const MAX_ZONES = 5;
/** A zone earns at most a few lines; the rest is its summary's job. */
export const MAX_ZONE_ITEMS = 4;
/** The small fixes are one or two lines, in passing. */
export const MAX_ALSO = 240;

function coerceZones(v: unknown): DispatchZone[] {
  if (!Array.isArray(v)) return [];
  const out: DispatchZone[] = [];
  for (const z of v) {
    if (!z || typeof z !== 'object') continue;
    const o = z as Record<string, unknown>;
    const zone = flat(o.zone ?? o.name ?? o.label ?? o.title);
    const line = flat(o.line ?? o.summary ?? o.note);
    const items = coerceItems(o.items).slice(0, MAX_ZONE_ITEMS);
    if (!zone || (!line && items.length === 0)) continue;
    out.push({ zone, ...(line ? { line } : {}), ...(items.length ? { items } : {}) });
    if (out.length >= MAX_ZONES) break;
  }
  return out;
}

export interface NormalizeContext {
  today: string;
  sinceDate: string | null;
  commitCount: number;
}

/**
 * Coerce whatever the model returned into a `DispatchData` the renderer can
 * trust. Overflow from an over-long headline is DEMOTED into the dek and then
 * the thread — never dropped, because it is usually the actual letter.
 */
export function normalizeDispatch(raw: unknown, ctx: NormalizeContext): DispatchData {
  const d = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  // ── headline: one line, capped, with the overflow kept ────────────────────
  let overflow = '';
  let headline = flat(d.headline);

  if (headline) {
    const sentence = firstSentence(headline);
    headline = sentence.head;
    overflow = sentence.rest;
    const cut = clip(headline, MAX_HEADLINE);
    headline = cut.head;
    if (cut.rest) overflow = `${cut.rest} ${overflow}`.trim();
  }
  if (!headline) headline = 'today on innernet';

  // ── dek: the model's, else the first slice of the overflow ────────────────
  let dek = flat(d.dek);
  if (!dek && overflow) {
    const sentence = firstSentence(overflow);
    const cut = clip(sentence.head, MAX_DEK);
    dek = cut.head;
    overflow = `${cut.rest} ${sentence.rest}`.trim();
  } else if (dek.length > MAX_DEK) {
    const cut = clip(dek, MAX_DEK);
    dek = cut.head;
    overflow = `${cut.rest} ${overflow}`.trim();
  }

  // ── thread: the model's, with any remaining overflow folded in ────────────
  const modelThread = flat(d.thread);
  const thread = [overflow, modelThread].filter(Boolean).join(' ').trim();

  const also = clip(flat(d.also), MAX_ALSO).head;

  const out: DispatchData = {
    date: ctx.today,
    headline,
    ...(dek ? { dek } : {}),
    stats: coerceStats(d.stats),
    zones: coerceZones(d.zones),
    ...(also ? { also } : {}),
    shipped: coerceItems(d.shipped),
    fixed: coerceItems(d.fixed),
    in_flight: coerceItems(d.in_flight),
    notes: coerceItems(d.notes),
    ...(thread ? { thread } : {}),
    signoff: flat(d.signoff) || 'jonna',
    signrole: flat(d.signrole) || 'product · innernet',
    provenance:
      flat(d.provenance) ||
      `written by innernet from its own memory + ${ctx.commitCount} commits since ${ctx.sinceDate ?? 'the beginning'}.`,
  };

  // A drifted top-level `sections` array makes `isRelease()` route the post into
  // ReleaseBody, which maps `sec.items` unguarded and throws inside a client
  // component — a blank reader with no error. It can never reach the renderer.
  delete (out as Record<string, unknown>).sections;

  return out;
}

/** How much of this edition the reader can actually show. */
export function renderableCount(d: DispatchData): number {
  return (
    (d.stats?.length ?? 0) +
    (d.zones ?? []).reduce((n, z) => n + (z.line ? 1 : 0) + (z.items?.length ?? 0), 0) +
    (d.also ? 1 : 0) +
    (d.shipped?.length ?? 0) +
    (d.fixed?.length ?? 0) +
    (d.in_flight?.length ?? 0) +
    (d.notes?.length ?? 0) +
    (d.thread ? 1 : 0)
  );
}

/** Is there enough here to be worth publishing at all? */
export function isPublishable(d: DispatchData): boolean {
  return renderableCount(d) > 0;
}
